import React, { useEffect, useMemo } from "react"

import { useInView } from "react-intersection-observer"
import { useParams } from "react-router-dom"

import { SpinnerElement } from "@front-shared/elements/spinner.element"

import { PolicySidebarViewMode } from "../../../../config/policies.config"
import { RuntimeHelper } from "../../../../helpers/runtime.helper"
import { useAppDispatch } from "../../../../store/appDispatch.hook"
import { useAppSelector } from "../../../../store/selectors/app.selector"
import { policiesSelector } from "../../../../store/selectors/policies.selector"
import { runtimeSelector } from "../../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../../store/selectors/selectedPracticeRole.selector"
import { policiesGetSinglePolicyRelatedHistory } from "../../../../store/thunks/policies.thunks"
import { HistoryItemCardComponent } from "./historyItemCard.component"

export const PolicySidebarHistoryComponent: React.FunctionComponent<
    IPolicySidebarHistoryComponentProps
> = props => {
    const runtimeState = useAppSelector(runtimeSelector)
    const dispatch = useAppDispatch()
    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const { nirvanaRequestId } = useParams()

    const { selectedItem, selectedItemHistory, selectedItemHistoryPagination } =
        useAppSelector(policiesSelector)

    const isListLoading = useMemo<boolean>(() => {
        return RuntimeHelper.isGetSinglePolicyRelatedHistoryLoading()
    }, [runtimeState.isLoading])

    const { ref: lastHistoryItemRef, inView } = useInView()

    useEffect(() => {
        if (
            nirvanaRequestId &&
            selectedPracticeRole &&
            inView &&
            selectedItemHistoryPagination.moreAvailable
        ) {
            void dispatch(
                policiesGetSinglePolicyRelatedHistory({
                    practice: selectedPracticeRole.practice,
                    payload: {
                        nirvanaRequestId,
                        pagination: selectedItemHistoryPagination,
                    },
                }),
            )
        }
    }, [inView, nirvanaRequestId, selectedPracticeRole])

    return (
        <div
            className={`
                flex-1 min-h-0 overflow-y-auto flex-col
                ${
                    !props.isHistoryTabOpen
                        ? "hidden"
                        : props.viewMode === PolicySidebarViewMode.CLOSED
                          ? "hidden md:flex"
                          : "flex"
                }
            `}
        >
            {selectedItemHistory.map((history, index) => {
                return (
                    <HistoryItemCardComponent
                        key={index}
                        ref={
                            index === selectedItemHistory.length - 1
                                ? lastHistoryItemRef
                                : undefined
                        }
                        index={index}
                        item={history}
                        isSelected={
                            selectedItem?.coverageResult.resultId ===
                            history.coverageResult.resultId
                        }
                    />
                )
            })}

            {isListLoading ? (
                // This spinner is used for both the initial load and the load more
                <SpinnerElement containerClassName="justify-center items-center h-full flex py-[12px]" />
            ) : null}
        </div>
    )
}

interface IPolicySidebarHistoryComponentProps {
    className?: string
    viewMode: PolicySidebarViewMode
    isHistoryTabOpen: boolean
}
